import { onBeforeUnmount, onMounted, ref } from 'vue';

const VERSION_COOKIE = 'asset_version';
const COOKIE_MAX_AGE = 86400; // 24hr
const DEFAULT_POLL_INTERVAL = 2 * 60 * 1000; // 2 minutes

const setVersionCookie = (version) => {
  document.cookie = `${VERSION_COOKIE}=${version}; path=/; max-age=${COOKIE_MAX_AGE}`;
};

const getVersionCookie = () => {
  const match = document.cookie.match(new RegExp(`${VERSION_COOKIE}=([^;]+)`));
  return match ? match[1] : null;
};

const fetchVersion = async () => {
  const res = await fetch(route('build.version') + '?t=' + Date.now());
  if (!res.ok) return null;

  const data = await res.json();
  return data?.version ?? null;
};

/**
 * Detects new deployments by polling the build version endpoint and prompts
 * the user to hard refresh so cached assets are cleared.
 *
 * Mirrors the behaviour in Layouts/AppLayout.vue, adapted for the
 * shop-management shell (Composition API + a PrimeVue banner instead of notyf).
 */
export const useAppVersionCheck = ({ pollInterval = DEFAULT_POLL_INTERVAL } = {}) => {
  const updateAvailable = ref(false);

  let assetVersion = null;
  let nextVersion = null;
  let intervalId = null;

  const applyUpdate = () => {
    const target = nextVersion ?? assetVersion;
    const base = window.location.href.split('?')[0];
    window.location.href = target ? `${base}?v=${target}` : base;
  };

  const stopPolling = () => {
    if (intervalId) {
      clearInterval(intervalId);
      intervalId = null;
    }
  };

  const poll = async () => {
    try {
      const version = await fetchVersion();
      if (!version) return;

      if (assetVersion && version !== assetVersion) {
        nextVersion = version;
        setVersionCookie(version);
        stopPolling();
        updateAvailable.value = true;
      }
    } catch (e) {
      // silently fail - try again next interval
    }
  };

  const startVersionCheck = async () => {
    try {
      const version = await fetchVersion();
      if (!version) return;

      assetVersion = version;

      const cookieVersion = getVersionCookie();
      if (cookieVersion && cookieVersion !== assetVersion) {
        // A new deploy landed while the tab was closed - hard refresh once.
        setVersionCookie(assetVersion);
        nextVersion = assetVersion;
        applyUpdate();
        return;
      }

      setVersionCookie(assetVersion);
    } catch (e) {
      return; // silently fail - don't break the shell
    }

    intervalId = setInterval(poll, pollInterval);
  };

  onMounted(startVersionCheck);
  onBeforeUnmount(stopPolling);

  return {
    updateAvailable,
    applyUpdate,
  };
};
